import React, { useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  Portal,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Control, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string;

export type PostcodeFormData = {
  address: string;
  postCode?: string;
  city: string;
  state: string;
  country: string;
  latitude?: number;
  longitude?: number;
  timezone: string;
};

type PostcodeInputProps<T extends { postCode: string }> = {
  control: Control<T>;
  error?: string;
  handleSelectLocation: (location: PostcodeFormData) => void;
};

// Postcode input form component that uses Google Places API to autocomplete postal codes
export function PostcodeInput<T extends { postCode: string }>({
  control,
  error,
  handleSelectLocation,
}: PostcodeInputProps<T>) {
  const { t } = useTranslation();
  const backgroundColor = useColorModeValue('#FFFFFF', 'customGrey.900');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputRect, setInputRect] = useState<DOMRect | null>(null);
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: GOOGLE_MAPS_API_KEY,
    options: {
      input: '',
      types: ['postal_code'],
    },
  });

  const [shouldDisplayAutocompleteOptions, setShouldDisplayAutocompleteOptions] = useState(false);

  async function onSelectLocation(location: google.maps.places.AutocompletePrediction) {
    placesService?.getDetails(
      {
        placeId: location.place_id,
        fields: ['address_components', 'geometry', 'place_id'],
      },
      async (details) => {
        const locationData: Record<string, string | undefined> = {};

        details?.address_components?.forEach((component) => {
          if (component.types.includes('locality')) {
            locationData.city = component?.long_name;
          } else if (component.types.includes('administrative_area_level_1')) {
            locationData.state = component?.short_name;
          } else if (component.types.includes('country')) {
            locationData.country = component?.long_name;
          } else if (component.types.includes('postal_code')) {
            locationData.postCode = component?.long_name;
          }
        });

        const latitude = details?.geometry?.location?.lat();
        const longitude = details?.geometry?.location?.lng();
        const termsLength = location.terms.length;

        const {
          data: { timeZoneId: timezone },
        } = await axios.get(
          `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude},${longitude}&timestamp=${Math.floor(
            Date.now() / 1000
          )}&language=es&key=${GOOGLE_MAPS_API_KEY}`
        );

        handleSelectLocation({
          address: location.description,
          city: locationData.city ?? location.terms[termsLength - 3]?.value,
          state: locationData.state ?? location.terms[termsLength - 2]?.value,
          country: locationData.country ?? location.terms[termsLength - 1]?.value,
          timezone,
          latitude,
          longitude,
          postCode: locationData.postCode,
        });

        setShouldDisplayAutocompleteOptions(false);
      }
    );
  }

  return (
    <FormControl isInvalid={!!error}>
      <Box position="relative" width="100%">
        <InputGroup>
          <Controller
            control={control}
            name={'postCode' as Path<T>}
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  ref={(el) => {
                    field.ref(el); // connect to react-hook-form
                    inputRef.current = el; // connect to our own local ref, helps with positioning
                  }}
                  borderRadius={0}
                  height="50px"
                  onFocus={() => setShouldDisplayAutocompleteOptions(true)}
                  onChange={(e) => {
                    field.onChange(e);
                    const newValue = e.currentTarget.value;
                    getPlacePredictions({ input: newValue });

                    if (inputRef.current) {
                      setInputRect(inputRef.current.getBoundingClientRect());
                    }
                  }}
                  type="text"
                  data-private
                  autoComplete="off"
                  data-testid="postcode-input"
                  background={backgroundColor}
                  borderColor={'customGrey.400'}
                  placeholder={`${t('Common.zipcode')}...`}
                />
              );
            }}
          />
        </InputGroup>

        {inputRect && shouldDisplayAutocompleteOptions && (
          <Portal>
            <Box
              position="absolute"
              top={`${inputRect.bottom + window.scrollY}px`}
              left={`${inputRect.left + window.scrollX}px`}
              width={`${inputRect.width}px`}
              zIndex={1400}
              background={backgroundColor}
              data-private
              maxHeight="200px"
              overflow="auto"
            >
              {!isPlacePredictionsLoading ? (
                <PredictionsList placePredictions={placePredictions} onSelectLocation={onSelectLocation} />
              ) : (
                <Flex direction={'column'} justify={'center'} align={'center'} border="1px solid #9FA0A4" py={6}>
                  <Spinner size={'lg'} />
                  <Text fontSize={'sm'}>{t('Common.loading suggestions')}...</Text>
                </Flex>
              )}
            </Box>
          </Portal>
        )}
      </Box>
      <FormHelperText mb={1}>{t('Common.zipcode is used')}</FormHelperText>
      <FormErrorMessage data-testid="postcode-error">{error}</FormErrorMessage>
    </FormControl>
  );
}

// Predictions list sub-component
type PredictionsListProps = {
  placePredictions: google.maps.places.AutocompletePrediction[];
  onSelectLocation: (location: google.maps.places.AutocompletePrediction) => void;
};

function PredictionsList({ placePredictions, onSelectLocation }: PredictionsListProps) {
  const { t } = useTranslation();

  if (!placePredictions.length) return null;

  return (
    <Box data-testid="postcode-prediction-list" border="1px solid #9FA0A4">
      {placePredictions.map((location, i) => (
        <Box
          key={location.place_id}
          borderBottom="1px solid grey"
          onClick={() => onSelectLocation(location)}
          cursor="pointer"
          py={2}
          px={2}
          data-testid={`postcode-prediction-${i}`}
        >
          <Flex align="center" as="button" type="button">
            <Box mr={1}>
              <FaMapMarkerAlt />
            </Box>
            <Text fontWeight={600} fontSize="sm">
              {location.description}
            </Text>
          </Flex>
        </Box>
      ))}
      <Text m={2} fontSize="xs">
        {t('Common.powered by google')}
      </Text>
    </Box>
  );
}
